import { FormEvent, useEffect, useId, useRef, useState } from "react"
import { Loader2Icon } from "lucide-react"

import { Alert, AlertDescription, AlertTitle } from "@/components/ui/shadcn/alert"
import { Badge } from "@/components/ui/shadcn/badge"
import { Button } from "@/components/ui/shadcn/button"
import { Checkbox } from "@/components/ui/shadcn/checkbox"
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/shadcn/dialog"
import { Field, FieldContent, FieldError, FieldGroup, FieldLabel } from "@/components/ui/shadcn/field"
import { Input } from "@/components/ui/shadcn/input"
import appStore from "@/stores/appStore"
import { updateEmailMarketingConsent, updateMarketingEmail } from "./emailMarketingService"

type EmailMarketingDialogProps = {
  open: boolean
  initialEmail?: string | null
  onOpenChange: (open: boolean) => void
  onSaved?: () => void
}

const EMAIL_ERROR = "Введите корректный email."
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
const EMAIL_SAVE_ERROR = "Не удалось сохранить email. Попробуйте ещё раз."

const EmailMarketingDialog = ({ open, initialEmail = "", onOpenChange, onSaved }: EmailMarketingDialogProps) => {
  const emailInputId = useId()
  const marketingConsentId = useId()
  const emailInputRef = useRef<HTMLInputElement>(null)
  const [email, setEmail] = useState(initialEmail ?? "")
  const [marketingAccepted, setMarketingAccepted] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitAttempted, setSubmitAttempted] = useState(false)
  const [error, setError] = useState("")

  const normalizedEmail = email.trim()
  const isEmailValid = EMAIL_PATTERN.test(normalizedEmail)
  const emailError = submitAttempted && !isEmailValid ? EMAIL_ERROR : ""
  const canSubmit = isEmailValid && marketingAccepted && !isSubmitting

  useEffect(() => {
    if (!open) return

    setEmail(initialEmail ?? "")
    setMarketingAccepted(Boolean(appStore.userInfo.email_accepted))
    setIsSubmitting(false)
    setSubmitAttempted(false)
    setError("")
    window.setTimeout(() => emailInputRef.current?.focus(), 0)
  }, [initialEmail, open])

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    setSubmitAttempted(true)

    if (!canSubmit) return

    setIsSubmitting(true)
    setError("")

    try {
      await updateEmailMarketingConsent(true)

      try {
        await updateMarketingEmail(normalizedEmail)
      } catch (emailSaveError) {
        try {
          await updateEmailMarketingConsent(false)
        } catch {
          // Consent revocation is best effort and must not mask the email-save error.
        }

        throw emailSaveError
      }

      await appStore.getUser()
      onSaved?.()
      onOpenChange(false)
    } catch (submissionError) {
      setError(getEmailSaveError(submissionError))
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <div className="flex flex-wrap items-center gap-2 pr-10">
            <DialogTitle>Добавьте email</DialogTitle>
            <Badge variant="secondary">Не обязательно</Badge>
          </div>
          <DialogDescription className="max-w-none">
            На этот email можно получать уведомления о статусе заказа и коды для входа, если СМС не приходит. А еще будем присылать выгодные предложения.
          </DialogDescription>
        </DialogHeader>

        <form className="flex flex-col gap-5" onSubmit={(event) => void handleSubmit(event)}>
          {error ? (
            <Alert variant="destructive">
              <AlertTitle>Не получилось</AlertTitle>
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          ) : null}

          <FieldGroup>
            <Field data-invalid={Boolean(emailError)}>
              <FieldLabel htmlFor={emailInputId}>Email</FieldLabel>
              <Input
                ref={emailInputRef}
                id={emailInputId}
                type="email"
                inputMode="email"
                autoComplete="email"
                placeholder="name@example.com"
                value={email}
                aria-invalid={Boolean(emailError)}
                aria-describedby={emailError ? `${emailInputId}-error` : undefined}
                disabled={isSubmitting}
                onChange={(event) => {
                  setEmail(event.target.value)
                  setError("")
                }}
              />
              <FieldError id={`${emailInputId}-error`}>{emailError}</FieldError>
            </Field>

            <Field orientation="horizontal">
              <Checkbox
                id={marketingConsentId}
                checked={marketingAccepted}
                disabled={isSubmitting}
                onCheckedChange={setMarketingAccepted}
              />
              <FieldContent>
                <FieldLabel htmlFor={marketingConsentId} className="block text-sm font-normal leading-normal text-muted-foreground">
                  Соглашаюсь получать рекламные и маркетинговые материалы по email. {" "}
                  <a href="/about/privacy" target="_blank" rel="noreferrer" className="box-decoration-clone border-b border-current pb-px text-primary hover:text-primary-hover">
                    Политика конфиденциальности
                  </a>
                </FieldLabel>
              </FieldContent>
            </Field>
          </FieldGroup>

          <DialogFooter className="gap-3">
            <Button type="button" variant="outline" disabled={isSubmitting} onClick={() => onOpenChange(false)}>
              Пропустить
            </Button>
            <Button type="submit" disabled={!canSubmit}>
              {isSubmitting ? <Loader2Icon data-icon="inline-start" className="animate-spin" /> : null}
              Добавить email
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

export default EmailMarketingDialog

const getEmailSaveError = (error: unknown) => {
  if (!error || typeof error !== "object") return EMAIL_SAVE_ERROR

  const response = "response" in error && error.response && typeof error.response === "object" ? error.response : null
  if (!response || !("data" in response) || !response.data || typeof response.data !== "object") return EMAIL_SAVE_ERROR

  const data = response.data

  if ("message" in data && typeof data.message === "string" && data.message.trim()) return data.message
  if ("error" in data && typeof data.error === "string" && data.error.trim()) return data.error

  return EMAIL_SAVE_ERROR
}
